/**
 * Dialogs the shell owns: 'confirm' (the prototype's archive / reset questions), 'upsell' (web.md §10:
 * "Free includes 3 sources" + More → /subscribe) and 'shortcuts' (the `?` list).
 */

import { ui } from './ui-store.js';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DigesterErrorShape, LimitName } from '@digester/core';
import { DialogFrame, registerDialog, type DialogProps } from './dialogs.js';
import { listShortcuts } from './shortcuts.js';

export interface ConfirmProps {
  title: string;
  message: string;
  note?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm(): Promise<void> | void;
}

export function ConfirmDialog({ close, dialogId, title, message, note, confirmLabel = 'Confirm', cancelLabel = 'Cancel', onConfirm }: DialogProps & ConfirmProps) {
  const [busy, setBusy] = useState(false);
  return (
    <DialogFrame title={title} onClose={close} dialogId={dialogId}>
      <p>{message}</p>
      {note ? <p className="field-help">{note}</p> : null}
      <div className="dialog-footer">
        <button type="button" className="secondary-button" onClick={close}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className="primary-button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              close();
            }
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </DialogFrame>
  );
}

const LIMIT_NOUNS: Partial<Record<LimitName, string>> = {
  sources: 'sources', filters: 'filters', tags: 'tags', topics: 'topics', folders: 'folders', views: 'saved views', scoring_rules: 'scoring rules',
  themes: 'themes', sync_devices: 'synced devices', connectors: 'connections', messages_per_day: 'messages a day',
};

/** "Free includes 3 sources" from a plan_limit / not_entitled error. */
export function upsellText(error: DigesterErrorShape | undefined): { title: string; body: string } {
  const limit = error?.details?.limit;
  const max = error?.details?.max;
  if (error?.code === 'plan_limit' && limit === 'active_models' && typeof max === 'number') {
    // models.md §10: Free runs one scoring model at a time (Jev does not count); Pro N runs 3 × N.
    return {
      title: 'More with Pro',
      body: `Your plan runs ${max} scoring model${max === 1 ? '' : 's'} at a time. Switch another model off first, or get Pro to run several side by side.`,
    };
  }
  if (error?.code === 'plan_limit' && limit && typeof max === 'number') {
    const noun = LIMIT_NOUNS[limit] ?? limit.replaceAll('_', ' ');
    return { title: 'More with Pro', body: `Your plan includes ${max} ${noun}. Pro raises every limit.` };
  }
  return { title: 'A Pro feature', body: error?.message ?? 'This needs a Pro plan.' };
}

export function UpsellDialog({ close, dialogId, error }: DialogProps & { error?: DigesterErrorShape }) {
  const navigate = useNavigate();
  const text = upsellText(error);
  return (
    <DialogFrame title={text.title} className="small-dialog upsell-dialog" onClose={close} dialogId={dialogId}>
      <p>{text.body}</p>
      <div className="dialog-footer">
        <button type="button" className="secondary-button" onClick={close}>
          Not now
        </button>
        <button
          type="button"
          className="primary-button"
          onClick={() => {
            close();
            // The edit that hit the limit cannot be saved on this plan: leave it without the
            // unsaved-changes question (which would offer a Save that fails again).
            ui.get().draft?.discard();
            navigate('/subscribe');
          }}
        >
          More
        </button>
      </div>
    </DialogFrame>
  );
}

const KEY_LABELS: Record<string, string> = { Escape: 'Esc', 'mod+s': 'Ctrl/Cmd + S' };

export function ShortcutsDialog({ close, dialogId }: DialogProps) {
  const list = listShortcuts();
  return (
    <DialogFrame title="Keyboard shortcuts" className="small-dialog shortcuts-dialog" onClose={close} dialogId={dialogId}>
      <dl className="shortcut-list">
        {list.map((s) => (
          <div key={s.key}>
            <dt>
              <kbd>{KEY_LABELS[s.key] ?? s.key.toUpperCase()}</kbd>
            </dt>
            <dd>{s.description}</dd>
          </div>
        ))}
      </dl>
    </DialogFrame>
  );
}

let registered = false;
export function registerBuiltinDialogs(): void {
  if (registered) return;
  registered = true;
  registerDialog<ConfirmProps>('confirm', ConfirmDialog);
  registerDialog<{ error?: DigesterErrorShape }>('upsell', UpsellDialog);
  registerDialog('shortcuts', ShortcutsDialog);
}
